// client/src/hooks/useSubscriptions.js
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';
import { ME_QUERY_KEY } from './useAuth';

export const useMutateSubscriptions = () => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateCaches = () => {
    queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
  };

  const { mutateAsync: createContaSubscription, ...createMutation } = useMutation({
    // ✅ AJUSTE: Adicionado 'billingAddress' aos parâmetros da função.
    mutationFn: async ({ planId, cardData, billingAddress }) => {
      // ✅ AJUSTE: 'billingAddress' é enviado no corpo da requisição.
      const response = await apiClient.post('/contas-subscriptions', { planId, cardData, billingAddress });
      return response.data;
    },
    onSuccess: () => {
      invalidateCaches();
      setAlert('Assinatura criada com sucesso!', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.error || err.message || 'Erro ao criar assinatura.';
      setAlert(msg, 'danger');
    },
  });

  const { mutateAsync: changePlan, ...changeMutation } = useMutation({
    // ✅ AJUSTE: Adicionado 'billingAddress' aos parâmetros da função.
    mutationFn: async ({ newPlanId, cardData, billingAddress }) => { 
      // ✅ AJUSTE: 'billingAddress' é enviado no corpo da requisição.
      const response = await apiClient.patch(`/contas-subscriptions/change-plan`, { 
        newPlanId, 
        cardData,
        billingAddress
      });
      return response.data;
    },
    onSuccess: () => { 
      invalidateCaches();
      setAlert('Plano alterado com sucesso!', 'success'); 
    },
    onError: (err) => setAlert(err.response?.data?.error || 'Erro ao alterar o plano.', 'danger'),
  });

  // 🔑 Cancelamento em 2 etapas
  const { mutateAsync: cancelSubscription, ...cancelMutation } = useMutation({
    mutationFn: async ({ immediate = false } = {}) => {
      if (!window.confirm(
        `Tem certeza que deseja ${immediate ? 'cancelar imediatamente' : 'agendar o cancelamento'} da sua assinatura?`
      )) {
        throw new Error('Ação cancelada pelo usuário');
      }
      const response = await apiClient.delete(`/contas-subscriptions?immediate=${immediate}`);
      return response.data; // { status, canceledAt, currentPeriodEnd, message }
    },
    onSuccess: (data) => {
      invalidateCaches();
      setAlert(data.message, 'success');
      return data; // repassa status/canceledAt/currentPeriodEnd
    },
    onError: (err) => {
      if (err.message !== 'Ação cancelada pelo usuário') {
        setAlert(err.response?.data?.error || 'Erro ao cancelar a assinatura.', 'danger');
      }
    },
  });

  const { mutateAsync: updatePaymentMethod, ...updateMutation } = useMutation({
    // ✅ AJUSTE: Adicionado 'billingAddress' aos parâmetros da função.
    mutationFn: async ({ cardData, billingAddress }) => {
      // ✅ AJUSTE: 'billingAddress' é enviado no corpo da requisição.
      const response = await apiClient.patch('/contas-subscriptions/payment-method', { cardData, billingAddress });
      return response.data;
    },
    onSuccess: () => {
      invalidateCaches();
      setAlert('Método de pagamento atualizado com sucesso!', 'success');
    },
    onError: (err) => {
      const msg = err.response?.data?.error || err.message || 'Erro ao atualizar o método de pagamento.';
      setAlert(msg, 'danger');
    },
  });

  return {
    createContaSubscription,
    updatePaymentMethod,
    changePlan,
    cancelSubscription,
    isLoading:
      createMutation.isPending ||
      updateMutation.isPending ||
      changeMutation.isPending ||
      cancelMutation.isPending,
    error:
      createMutation.error ||
      updateMutation.error ||
      changeMutation.error ||
      cancelMutation.error,
  };
};